import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiMail, FiCheckCircle, FiXCircle, FiRefreshCw, FiLogOut } from 'react-icons/fi';
import { getAuth } from 'firebase/auth';
import { firebaseApp } from '../../services/firebase/firebase.config';
import { Screen } from '../../components/ui/Screen';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { resendVerificationEmail, reloadUser, signOutUser } from '../../services/firebase/auth.service';
import { useAppContext } from '../../store/AppContext';

const auth = getAuth(firebaseApp);

export function EmailVerificationScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: userLoading } = useAppContext();
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendError, setResendError] = useState('');
  const [resendSuccess, setResendSuccess] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [autoResendAttempted, setAutoResendAttempted] = useState(false);

  // Redirect if already verified (this will trigger after reloadUser updates the auth state)
  useEffect(() => {
    if (!userLoading && user && user.emailVerified && !checking) {
      // Only auto-redirect if we're not in the middle of checking verification
      // This prevents conflicts with the manual check button
      navigate('/onboarding', { replace: true });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [user, userLoading, navigate, checking]);

  // Auto-resend verification email if coming from login with resend=true parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const shouldAutoResend = urlParams.get('resend') === 'true';
    
    // Only auto-resend once, when user is loaded and not verified
    if (shouldAutoResend && !userLoading && user && !user.emailVerified && !autoResendAttempted) {
      setAutoResendAttempted(true);
      
      // Small delay to ensure user sees the page before email is sent
      const timer = setTimeout(async () => {
        try {
          await resendVerificationEmail();
          setResendSuccess(true);
          setCooldownSeconds(60); // 60 second cooldown
          
          // Auto-hide success message after 5 seconds
          setTimeout(() => setResendSuccess(false), 5000);
          
          // Remove resend parameter from URL to prevent re-sending on refresh
          navigate('/auth/verify-email', { replace: true });
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Failed to send verification email. Please try again.';
          setResendError(message);
          
          // Check if error contains cooldown information
          if (message.includes('wait')) {
            const match = message.match(/(\d+)\s+second/);
            if (match) {
              setCooldownSeconds(parseInt(match[1], 10));
            }
          }
        }
      }, 500);
      
      return () => clearTimeout(timer);
    }
  }, [location.search, user, userLoading, autoResendAttempted, navigate]);

  // Cooldown timer for resend button
  useEffect(() => {
    if (cooldownSeconds > 0) {
      const timer = setTimeout(() => {
        setCooldownSeconds(cooldownSeconds - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldownSeconds]);

  async function handleCheckVerification() {
    setError('');
    setSuccess(false);
    setChecking(true);

    try {
      const updatedUser = await reloadUser();
      
      if (!updatedUser) {
        throw new Error('No user found. Please sign in again.');
      }

      if (updatedUser.emailVerified) {
        setSuccess(true);
        
        // Verify the user is still signed in
        const currentFirebaseUser = auth.currentUser;
        if (!currentFirebaseUser || !currentFirebaseUser.emailVerified) {
          throw new Error('User session expired. Please sign in again.');
        }
        
        // Wait for AppContext to update (onAuthStateChanged should fire after reload)
        // We'll wait up to 1.5 seconds for the auth state to propagate
        let attempts = 0;
        const maxAttempts = 15; // 1.5 seconds
        
        while (attempts < maxAttempts) {
          // Check if AppContext has updated
          if (user && user.emailVerified) {
            break;
          }
          await new Promise(resolve => setTimeout(resolve, 100));
          attempts++;
        }
        
        setChecking(false);
        
        // Navigate to onboarding - user is verified and should be logged in
        navigate('/onboarding', { replace: true });
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        setError('Your email has not been verified yet. Please check your inbox and click the verification link.');
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to check verification status. Please try again.';
      setError(message);
    } finally {
      setChecking(false);
    }
  }

  async function handleResendVerification() {
    setResendError('');
    setResendSuccess(false);
    setResending(true);

    try {
      await resendVerificationEmail();
      setResendSuccess(true);
      setCooldownSeconds(60); // 60 second cooldown
      
      // Auto-hide success message after 5 seconds
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to send verification email. Please try again.';
      
      // Check if error contains cooldown information
      if (message.includes('wait')) {
        // Extract cooldown from error message if present
        const match = message.match(/(\d+)\s+second/);
        if (match) {
          setCooldownSeconds(parseInt(match[1], 10));
        }
      }
      
      setResendError(message);
    } finally {
      setResending(false);
    }
  }

  async function handleLogout() {
    try {
      await signOutUser();
      navigate('/auth/login', { replace: true });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Failed to sign out:', error);
      setError('Failed to sign out. Please try again.');
    }
  }

  // Show loading state while checking auth
  if (userLoading) {
    return (
      <Screen title="Loading..." subtitle="Checking authentication...">
        <Card>
          <p className="text-center text-gray-500">Loading...</p>
        </Card>
      </Screen>
    );
  }

  // Don't render if user is already verified (will redirect)
  if (user && user.emailVerified) {
    return null;
  }

  // If no user, redirect to login
  if (!user) {
    navigate('/auth/login', { replace: true });
    return null;
  }

  return (
    <Screen
      title="Verify your email"
      subtitle="We've sent you a verification link"
      headerRight={
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="px-2 py-1 text-xs"
          onClick={handleLogout}
          leftIcon={<FiLogOut />}
        >
          Logout
        </Button>
      }
    >
      <Card>
        <div className="space-y-4">
          {/* Success icon and main message */}
          <div className="flex flex-col items-center text-center space-y-3">
            <div className="rounded-full bg-blue-100 p-3">
              <FiMail className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">
                Verification email sent!
              </p>
              <p className="mt-1 text-xs text-gray-600">
                We've sent a verification link to
              </p>
              <p className="mt-0.5 text-xs font-medium text-gray-900">
                {user.email}
              </p>
            </div>
          </div>

          {/* Important: Check spam/junk */}
          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
            <p className="text-xs text-amber-900 font-medium">
              ⚠️ Don't see the email?
            </p>
            <p className="mt-1 text-xs text-amber-800">
              Check your <strong>spam</strong> or <strong>junk</strong> folder. Verification emails sometimes land there.
            </p>
          </div>

          {/* Success message from check */}
          {success && (
            <div className="rounded-lg bg-green-50 border border-green-200 p-3 flex items-start gap-2">
              <FiCheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
              <p className="text-xs text-green-900">
                Email verified! Redirecting to onboarding...
              </p>
            </div>
          )}

          {/* Error message from check */}
          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 flex items-start gap-2">
              <FiXCircle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
              <p className="text-xs text-red-900">{error}</p>
            </div>
          )}

          {/* Resend success message */}
          {resendSuccess && (
            <div className="rounded-lg bg-green-50 border border-green-200 p-3 flex items-start gap-2">
              <FiCheckCircle className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
              <p className="text-xs text-green-900">
                Verification email sent! Please check your inbox.
              </p>
            </div>
          )}

          {/* Resend error message */}
          {resendError && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-3">
              <p className="text-xs text-red-900">{resendError}</p>
            </div>
          )}

          {/* Check verification button */}
          <Button
            fullWidth
            onClick={handleCheckVerification}
            disabled={checking}
            leftIcon={checking ? <FiRefreshCw className="animate-spin" /> : <FiCheckCircle />}
          >
            {checking ? 'Checking...' : 'I\'ve verified my email'}
          </Button>

          {/* Resend button */}
          <Button
            fullWidth
            onClick={handleResendVerification}
            disabled={resending || cooldownSeconds > 0}
            variant={cooldownSeconds > 0 ? 'secondary' : 'primary'}
            leftIcon={resending ? <FiRefreshCw className="animate-spin" /> : <FiMail />}
          >
            {resending
              ? 'Sending...'
              : cooldownSeconds > 0
              ? `Resend email (${cooldownSeconds}s)`
              : 'Resend verification email'}
          </Button>
        </div>
      </Card>
    </Screen>
  );
}
